import styled from "styled-components";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../constants/Styles";
import { flip, offset, shift, useFloating } from "@floating-ui/react";
import { Placement } from "@floating-ui/react-dom";

export const MenuButtonParent = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
`;

export const MenuContainer = styled.div`
  background-color: ${COLORS.inputGray};
  border-radius: 1rem;
  padding: 0.5rem 0;
  box-shadow: 0 0.25rem 0.25rem 0.25rem rgba(0, 0, 0, 0.25);
  width: max-content;
`;

export const MenuItem = styled.div<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? "initial" : "pointer")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  color: ${COLORS.secondaryLight};
  padding: 0.5rem 1rem;
  &:hover {
    color: ${({ disabled }) =>
      disabled ? COLORS.secondaryLight : COLORS.secondary};
  }
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

export default function DropdownMenu({
  placement = "bottom-start",
  dropdownTrigger,
  children,
  disabled,
}: {
  placement?: Placement;
  dropdownTrigger: ReactNode;
  children: ReactNode;
  disabled?: boolean;
}) {
  const dropdownTriggerRef = useRef<HTMLDivElement | null>(null);
  const [showAdditionalTools, setShowAdditionalTools] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: placement,
    middleware: [
      offset(4),
      flip(), // flips to top if no room below
      shift(), // nudges sideways if it would clip horizontally
    ],
  });
  const { setReference, setFloating } = refs;

  useEffect(() => {
    const toggleMenuListener = (event: MouseEvent) => {
      if (
        !disabled &&
        dropdownTriggerRef.current &&
        dropdownTriggerRef.current?.contains(event.target as Node)
      ) {
        setShowAdditionalTools(true);
      } else {
        setShowAdditionalTools(false);
      }
    };
    window.addEventListener("click", toggleMenuListener);
    return () => {
      window.removeEventListener("click", toggleMenuListener);
    };
  }, [disabled]);

  return (
    <MenuButtonParent>
      <div ref={setReference}>
        <div ref={dropdownTriggerRef}>{dropdownTrigger}</div>
      </div>
      {showAdditionalTools && !disabled && (
        <div ref={setFloating} style={{ ...floatingStyles, zIndex: 10 }}>
          <MenuContainer>{children}</MenuContainer>
        </div>
      )}
    </MenuButtonParent>
  );
}
